/*
 * Copyright (c) 2018-2023 MemberCenter, a subsidiary of Cameron Collective
 *   All Rights Reserved
 */
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { NumericFormat } from 'react-number-format';

import options from 'config/member_types.config';

import { styles } from './styles';

function LinkedMemberLimit(props) {
	const { inputRef, onChange, ...other } = props;
	return (
		<NumericFormat
			{...other}
			getInputRef={inputRef}
			onValueChange={values => {
				onChange({
					target: {
						value: values.value
					}
				});
			}}
			allowNegative={false}
			allowLeadingZeros={false}
			thousandSeparator
		/>
	);
}

function NumberFormatCustom(props) {
	const { inputRef, onChange, ...other } = props;
	return (
		<NumericFormat
			{...other}
			getInputRef={inputRef}
			onValueChange={values => {
				onChange({
					target: {
						value: values.value
					}
				});
			}}
			allowNegative="false"
			thousandSeparator
			prefix="$"
		/>
	);
}

const MemberTypeForm = ({
	handlePropChange,
	member_type,
	amount,
	renewal,
	billing,
	allow_linked,
	linked_limit,
	visible,
	free_type,
	directory_list,
	children
}) => {
	const handleChange = prop => event => {
		if (prop === 'allow_linked') {
			handlePropChange(prop, event.target.checked);
			return;
		}
		if (prop === 'visible') {
			handlePropChange(prop, event.target.checked);
			return;
		}
		if (prop === 'free_type') {
			handlePropChange(prop, event.target.checked);
			return;
		}
		if (prop === 'directory_list') {
			handlePropChange(prop, event.target.checked);
			return;
		}
		handlePropChange(prop, event.target.value);
	};
	return (
		<Grid container spacing={1}>
			<Grid item xs={12}>
				<FormControl sx={styles.formControl}>
					<TextField
						value={member_type}
						error={member_type.length === 0}
						onChange={handleChange('member_type')}
						autoComplete="member_type"
						name="member_type"
						variant="filled"
						required
						fullWidth
						id="firstName"
						label="Member Type"
						helperText="Ex: General Membership, Dual Member"
						autoFocus
					/>
				</FormControl>
			</Grid>
			<Grid item xs={12}>
				<FormControl sx={styles.formControl}>
					<InputLabel htmlFor="billing-simple">Billing</InputLabel>
					<Select
						value={billing}
						variant="filled"
						onChange={handleChange('billing')}
						inputProps={{
							variant: 'filled',
							name: 'billing_type',
							id: 'billing-simple'
						}}
					>
						{options.billing.map(option => (
							<MenuItem key={option.db_key} value={option.db_key}>
								{option.display}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid>
			<Grid item xs={6}>
				<FormControl fullWidth sx={styles.formControl}>
					<InputLabel htmlFor="renewal-simple">Renewal Type</InputLabel>
					<Select
						variant="filled"
						value={renewal}
						onChange={handleChange('renewal')}
						inputProps={{
							name: 'renewal_type',
							id: 'renewal-simple'
						}}
					>
						{options.renewal.map(option => (
							<MenuItem key={option.db_key} value={option.db_key}>
								{option.display}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid>
			<Grid item xs={6}>
				<FormControl fullWidth sx={styles.margin}>
					<TextField
						variant="filled"
						label="Membership Amount"
						value={amount}
						onChange={handleChange('amount')}
						id="formatted-numberformat-input"
						InputProps={{
							inputComponent: NumberFormatCustom
						}}
					/>
				</FormControl>
			</Grid>
			<Grid item xs={12}>
				<FormGroup row>
					<FormControlLabel
						control={
							<Checkbox
								checked={allow_linked}
								onChange={handleChange('allow_linked')}
								name="allow_linked"
							/>
						}
						label="Allow Linked Members?"
					/>
					{allow_linked && (
						<TextField
							fullWidth={true}
							variant="filled"
							label="Linked Members Limit"
							value={linked_limit}
							onChange={handleChange('linked_limit')}
							id="linked_limit_input"
							InputProps={{
								inputComponent: LinkedMemberLimit
							}}
						/>
					)}
				</FormGroup>
			</Grid>
			<Grid item xs={12}>
				<FormControlLabel
					control={<Checkbox checked={visible} onChange={handleChange('visible')} name="visible" />}
					label="Display in Sign Up Form?"
				/>
			</Grid>
			<Grid item xs={12}>
				<FormControlLabel
					control={
						<Checkbox
							checked={directory_list}
							onChange={handleChange('directory_list')}
							name="directory_list"
						/>
					}
					label="Display in Membership Directory?"
				/>
			</Grid>
			<Grid item xs={12}>
				<Tooltip
					arrow="true"
					sx={{ tooltip: styles.tooltip }}
					title="
                Free and Trial Memberships will not initally require payment, and will not display the paymemt section of the form.
                If Billing is set to Annual, the renewal date will be set to 365 days.
                If Billing is set to Monthly, the renewal date will be set to 30 days.
                If Renewal type is set to One-Time, the member will lapse when the renewal passes.
                If Renewal type is set to Automatic, the member will remain current indefinitely.
                "
				>
					<FormControlLabel
						control={<Checkbox checked={free_type} onChange={handleChange('free_type')} name="free_type" />}
						label="Free/Trial Membership Type? (Will not require payment)"
					/>
				</Tooltip>
			</Grid>
			{children}
		</Grid>
	);
};
export default MemberTypeForm;
